/**
 * ScheduleEditDialog - Create or edit an allocation schedule
 *
 * Movement ledger model:
 * - Create mode: segmented control selects Departure / Arrival / Round Trip.
 * - Edit mode: shows direction badge, edits schedule fields, quick actions to cancel/complete.
 */

import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  useCreateAircraftSchedule,
  useUpdateAircraftSchedule,
  useCancelAircraftSchedule,
} from '@/hooks/useAircraftSchedules'
import {
  SCHEDULE_DIRECTION_LABELS,
  SCHEDULE_STATUS_LABELS,
  SCHEDULE_STATUS_VARIANTS,
} from '@/types/aircraft-schedules'
import type { AircraftSchedule } from '@/types/aircraft-schedules'

interface ScheduleEditDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Pass a schedule to edit; omit or pass null/undefined to create a new one */
  schedule?: AircraftSchedule | null
  /** Required for create mode — the aircraft + location to attach the schedule to */
  aircraftId?: string
  locationId?: string
}

function toDatetimeLocal(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  const pad = (n: number) => n.toString().padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function ScheduleEditDialog({
  open,
  onOpenChange,
  schedule,
  aircraftId,
  locationId,
}: ScheduleEditDialogProps) {
  const isCreateMode = !schedule

  const [scheduledAt, setScheduledAt] = useState('')
  const [returnAt, setReturnAt] = useState('')
  const [airportIcao, setAirportIcao] = useState('')
  const [airportName, setAirportName] = useState('')
  const [confidence, setConfidence] = useState<'confirmed' | 'tentative'>('confirmed')
  const [notes, setNotes] = useState('')
  const [createMode, setCreateMode] = useState<'departure' | 'arrival' | 'roundtrip'>('departure')

  const createSchedule = useCreateAircraftSchedule()
  const updateSchedule = useUpdateAircraftSchedule()
  const cancelSchedule = useCancelAircraftSchedule()

  // Populate form when schedule changes (edit mode) or reset for create mode
  useEffect(() => {
    if (schedule) {
      setScheduledAt(toDatetimeLocal(schedule.scheduledAt))
      setReturnAt('')
      setAirportIcao(schedule.airportIcao ?? '')
      setAirportName(schedule.airportName ?? '')
      setConfidence(schedule.confidence === 'tentative' ? 'tentative' : 'confirmed')
      setNotes(schedule.notes ?? '')
    } else {
      setScheduledAt('')
      setReturnAt('')
      setAirportIcao('')
      setAirportName('')
      setConfidence('confirmed')
      setNotes('')
      setCreateMode('departure')
    }
  }, [schedule, open])

  const handleCreate = async () => {
    if (!aircraftId || !locationId) {
      toast.error('Missing aircraft/location context')
      return
    }
    if (!scheduledAt) {
      toast.error(createMode === 'arrival' ? 'Arrival date is required' : 'Departure date is required')
      return
    }
    if (createMode === 'roundtrip' && !returnAt) {
      toast.error('Return date is required for round trips')
      return
    }
    try {
      if (createMode === 'arrival') {
        // Create inbound-only record
        await createSchedule.mutateAsync({
          aircraft_id: aircraftId,
          location_id: locationId,
          direction: 'inbound',
          scheduled_at: new Date(scheduledAt).toISOString(),
          source: 'csr_entered',
          confidence,
          status: 'scheduled',
          airport_icao: airportIcao || undefined,
          airport_name: airportName || undefined,
          notes: notes || undefined,
        })
      } else {
        // Create outbound record (departure or roundtrip)
        const outbound = await createSchedule.mutateAsync({
          aircraft_id: aircraftId,
          location_id: locationId,
          direction: 'outbound',
          scheduled_at: new Date(scheduledAt).toISOString(),
          source: 'csr_entered',
          confidence,
          status: 'scheduled',
          airport_icao: airportIcao || undefined,
          airport_name: airportName || undefined,
          notes: notes || undefined,
        })

        // If round trip, create paired inbound
        if (createMode === 'roundtrip' && returnAt) {
          await createSchedule.mutateAsync({
            aircraft_id: aircraftId,
            location_id: locationId,
            direction: 'inbound',
            scheduled_at: new Date(returnAt).toISOString(),
            source: 'csr_entered',
            confidence,
            status: 'scheduled',
            pair_id: outbound.id,
            airport_icao: airportIcao || undefined,
            airport_name: airportName || undefined,
          })
        }
      }

      toast.success('Schedule created')
      onOpenChange(false)
    } catch {
      toast.error('Failed to create schedule')
    }
  }

  const handleSave = async () => {
    if (!schedule) return
    try {
      await updateSchedule.mutateAsync({
        id: schedule.id,
        scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : undefined,
        airport_icao: airportIcao || undefined,
        airport_name: airportName || undefined,
        confidence,
        notes: notes || undefined,
      })
      toast.success('Schedule updated')
      onOpenChange(false)
    } catch {
      toast.error('Failed to update schedule')
    }
  }

  const handleCancel = async () => {
    if (!schedule) return
    try {
      await cancelSchedule.mutateAsync(schedule.id)
      toast.success('Schedule cancelled')
      onOpenChange(false)
    } catch {
      toast.error('Failed to cancel schedule')
    }
  }

  const handleMarkCompleted = async () => {
    if (!schedule) return
    try {
      await updateSchedule.mutateAsync({
        id: schedule.id,
        status: 'completed',
      })
      toast.success('Schedule marked as completed')
      onOpenChange(false)
    } catch {
      toast.error('Failed to mark completed')
    }
  }

  const isCancellable = schedule && (schedule.status === 'scheduled' || schedule.status === 'confirmed')
  const isCompletable = schedule && schedule.status === 'confirmed'
  const isPending = createSchedule.isPending || updateSchedule.isPending || cancelSchedule.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isCreateMode ? 'Add Schedule' : 'Edit Schedule'}
            {schedule && (
              <>
                <Badge variant="outline" className="text-[9px] px-1.5 py-0 font-normal">
                  {SCHEDULE_DIRECTION_LABELS[schedule.direction]}
                </Badge>
                <Badge
                  variant={SCHEDULE_STATUS_VARIANTS[schedule.status]}
                  className="text-[9px] px-1.5 py-0 font-normal"
                >
                  {SCHEDULE_STATUS_LABELS[schedule.status]}
                </Badge>
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {isCreateMode
              ? 'Schedule a departure, arrival, or round trip.'
              : 'Update schedule details or mark as completed/cancelled.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Direction selector — create mode only */}
          {isCreateMode && (
            <div className="flex rounded-lg bg-muted p-0.5 gap-0.5">
              {(['departure', 'arrival', 'roundtrip'] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => { setCreateMode(mode); if (mode !== 'roundtrip') setReturnAt('') }}
                  className={`flex-1 text-xs font-medium py-1.5 px-2 rounded-md transition-colors ${
                    createMode === mode
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {mode === 'departure' ? 'Departure' : mode === 'arrival' ? 'Arrival' : 'Round Trip'}
                </button>
              ))}
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="sched-time" className="text-xs">
              {(isCreateMode ? createMode === 'arrival' : schedule?.direction === 'inbound')
                ? 'Arrival Date & Time'
                : 'Departure Date & Time'}
            </Label>
            <Input
              id="sched-time"
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              className="h-8 text-sm"
            />
          </div>

          {/* Return date — create mode round trip only */}
          {isCreateMode && createMode === 'roundtrip' && (
            <div className="space-y-1.5">
              <Label htmlFor="sched-return" className="text-xs">Return Date & Time</Label>
              <Input
                id="sched-return"
                type="datetime-local"
                value={returnAt}
                onChange={(e) => setReturnAt(e.target.value)}
                className="h-8 text-sm"
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="sched-icao" className="text-xs">
                {(isCreateMode ? createMode === 'arrival' : schedule?.direction === 'inbound')
                  ? 'Origin ICAO'
                  : createMode === 'roundtrip' ? 'Airport ICAO' : 'Destination ICAO'}
              </Label>
              <Input
                id="sched-icao"
                placeholder="e.g. KLAX"
                value={airportIcao}
                onChange={(e) => setAirportIcao(e.target.value.toUpperCase())}
                maxLength={4}
                className="h-8 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sched-name" className="text-xs">
                {(isCreateMode ? createMode === 'arrival' : schedule?.direction === 'inbound')
                  ? 'Origin Name'
                  : createMode === 'roundtrip' ? 'Airport Name' : 'Destination Name'}
              </Label>
              <Input
                id="sched-name"
                placeholder="e.g. Los Angeles Intl"
                value={airportName}
                onChange={(e) => setAirportName(e.target.value)}
                className="h-8 text-sm"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Confidence</Label>
            <div className="flex gap-3">
              <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                <input
                  type="radio"
                  name="sched-confidence"
                  checked={confidence === 'confirmed'}
                  onChange={() => setConfidence('confirmed')}
                  className="w-3 h-3"
                />
                Confirmed
              </label>
              <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                <input
                  type="radio"
                  name="sched-confidence"
                  checked={confidence === 'tentative'}
                  onChange={() => setConfidence('tentative')}
                  className="w-3 h-3"
                />
                Tentative
              </label>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="sched-notes" className="text-xs">Notes</Label>
            <Textarea
              id="sched-notes"
              placeholder="Optional notes..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="text-sm min-h-[60px]"
              rows={2}
            />
          </div>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          {isCreateMode ? (
            <Button
              onClick={handleCreate}
              disabled={isPending}
              className="w-full"
            >
              {createSchedule.isPending
                ? 'Creating...'
                : createMode === 'arrival'
                  ? 'Create Arrival'
                  : createMode === 'roundtrip'
                    ? 'Create Round Trip'
                    : 'Create Departure'}
            </Button>
          ) : (
            <>
              <div className="flex gap-2 w-full">
                <Button
                  onClick={handleSave}
                  disabled={isPending}
                  className="flex-1"
                >
                  {updateSchedule.isPending ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
              <div className="flex gap-2 w-full">
                {isCompletable && (
                  <Button
                    variant="outline"
                    onClick={handleMarkCompleted}
                    disabled={isPending}
                    className="flex-1 text-green-700 border-green-300 hover:bg-green-50 dark:text-green-400 dark:border-green-700 dark:hover:bg-green-950"
                  >
                    Mark Completed
                  </Button>
                )}
                {isCancellable && (
                  <Button
                    variant="outline"
                    onClick={handleCancel}
                    disabled={isPending}
                    className="flex-1 text-destructive border-destructive/30 hover:bg-destructive/10"
                  >
                    {cancelSchedule.isPending ? 'Cancelling...' : 'Cancel Schedule'}
                  </Button>
                )}
              </div>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
